import api from './client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WebhookEventStatus = 'ok' | 'no_connection' | 'trip_error' | 'sig_invalid';

export interface WebhookEvent {
  _id: string;
  received_at: string;
  event_type: string;
  smartcar_vehicle_id: string;
  vehicle_id: string | null;
  processing_status: WebhookEventStatus;
  trip_created: boolean;
  trip_id: string | null;
  raw_payload: Record<string, unknown> | null;
  patch: Record<string, unknown> | null;
  processing_error: string | null;
}

export interface WebhookEventsResponse {
  success: boolean;
  data: {
    events: WebhookEvent[];
    total: number;
    page: number;
    pages: number;
  };
}

export interface WebhookEventsFilters {
  page?: number;
  limit?: number;
  vehicle_id?: string;
  status?: string;
  from?: string;
  to?: string;
}

export interface SmartcarData {
  odometer_km?: number;
  latitude?: number;
  longitude?: number;
  fuel_percent?: number;
  battery_percent?: number;
  battery_range_km?: number;
  charge_state?: 'CHARGING' | 'FULLY_CHARGED' | 'NOT_CHARGING';
  location_type?: 'CURRENT' | 'LAST_PARKED';
  fetched_at?: string;
}

export interface SmartcarConnection {
  vehicle_id: string;
  smartcar_vehicle_id: string;
  status: 'active' | 'expired' | 'revoked';
  last_sync?: string;
  last_data?: SmartcarData;
  vehicle_info?: {
    make?: string;
    model?: string;
    year?: number;
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export const getSmartcarConnectUrl = async (
  vehicleId: string,
  mode: 'live' | 'simulated' = 'simulated'
): Promise<{ url: string }> => {
  const response = await api.get<{ success: boolean; data: { url: string } }>(
    `/api/smartcar/connect`,
    { params: { vehicleId, mode } }
  );
  return response.data.data;
};

export const getSmartcarData = async (vehicleId: string): Promise<SmartcarData | null> => {
  const response = await api.get<{ success: boolean; data: { last_data?: SmartcarData; status: string } }>(
    `/api/smartcar/vehicles/${vehicleId}/data`
  );
  return response.data.data?.last_data ?? null;
};

export const refreshSmartcarData = async (vehicleId: string): Promise<SmartcarData | null> => {
  const response = await api.post<{ success: boolean; data: { last_data?: SmartcarData } }>(
    `/api/smartcar/vehicles/${vehicleId}/refresh`
  );
  return response.data.data?.last_data ?? null;
};

export const disconnectSmartcar = async (vehicleId: string): Promise<void> => {
  await api.delete(`/api/smartcar/vehicles/${vehicleId}`);
};

export const listSmartcarConnections = async (): Promise<SmartcarConnection[]> => {
  const response = await api.get<{ success: boolean; data: { count: number; connections: SmartcarConnection[] } }>(
    `/api/smartcar/vehicles`
  );
  return response.data.data?.connections ?? [];
};

export const getWebhookEvents = async (
  filters: WebhookEventsFilters = {}
): Promise<WebhookEventsResponse['data']> => {
  const params = new URLSearchParams();
  if (filters.page)       params.set('page',       String(filters.page));
  if (filters.limit)      params.set('limit',      String(filters.limit));
  if (filters.vehicle_id) params.set('vehicle_id', filters.vehicle_id);
  if (filters.status)     params.set('status',     filters.status);
  if (filters.from)       params.set('from',       filters.from);
  if (filters.to)         params.set('to',         filters.to);
  const response = await api.get<WebhookEventsResponse>(
    `/api/smartcar/webhook-events?${params.toString()}`
  );
  return response.data.data;
};
